import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Loader2, Calendar, Plus, 
  MessageSquare, User, Clock, CheckCircle2, Info, X
} from 'lucide-react';
import { salesService } from '../../services/salesService';
import { useAuth } from '../../context/AuthContext';
import StatusBadge from '../../components/StatusBadge';

const STATUS_OPTIONS = ['New', 'In Discussion', 'Meeting Scheduled', 'Negotiation', 'Won', 'Lost'];

const LeadDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [lead, setLead] = useState(null);
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMeetModal, setShowMeetModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const loadData = async () => {
    try {
      const [leadRes, meetRes] = await Promise.all([
        salesService.getLead(id),
        salesService.getLeadMeetings(id)
      ]);
      setLead(leadRes.data);
      setMeetings(meetRes.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [id]);

  const handleStatusChange = async (newStatus) => {
    try {
      await salesService.updateLead(id, { status: newStatus });
      loadData();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleLogMeeting = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const formData = new FormData(e.target);
    try {
      await salesService.logMeeting({
        lead_id: id,
        meeting_date: formData.get('date'),
        meeting_time: formData.get('time'),
        attendees: formData.get('attendees').split(','),
        platform: formData.get('platform'),
        outcome_notes: formData.get('outcome_notes'),
        requires_ceo: formData.get('requires_ceo') === 'on'
      });
      setShowMeetModal(false);
      loadData();
    } catch (err) {
      alert(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="flex justify-center p-20"><Loader2 className="animate-spin text-primary" /></div>;
  if (!lead) return <div className="p-10 text-center">Lead not found.</div>;

  return (
    <div className="max-w-6xl mx-auto space-y-6 pb-20">
      <button onClick={() => navigate('/sales')} className="flex items-center gap-2 text-gray-500 hover:text-primary transition-colors text-sm font-bold uppercase">
        <ArrowLeft className="w-4 h-4" /> Pipeline
      </button>

      {/* Lead Header */}
      <div className="bg-white rounded-card border border-gray-100 shadow-soft p-8 flex justify-between items-start flex-wrap gap-4 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-2 h-full bg-primary" />
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-3xl font-black text-gray-900 tracking-tight">{lead.client_name}</h1>
            <StatusBadge status={String(lead.status)} />
          </div>
          <p className="text-gray-500 font-bold uppercase text-xs tracking-widest">{lead.service_required} &bull; Source: {lead.platform}</p>
        </div>
        
        {/* Status Actions */}
        <div className="flex items-center gap-2">
          <p className="text-[10px] font-black text-gray-300 uppercase tracking-widest mr-2">Update Status:</p>
          <select 
            value={lead.status} 
            onChange={(e) => handleStatusChange(e.target.value)}
            className="p-2 border rounded-xl text-xs font-bold bg-gray-50 outline-none focus:ring-2 focus:ring-primary/20"
          >
            {STATUS_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column: Info */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white rounded-card border border-gray-100 shadow-soft p-6 space-y-4">
             <h2 className="text-[10px] font-black text-primary uppercase tracking-[0.2em] border-b pb-4">Deal Overview</h2>
             <div className="space-y-3">
                <InfoItem label="Estimated Value" value={lead.estimated_value.toLocaleString() + ' ' + lead.currency} />
                <InfoItem label="First Contact" value={lead.first_contact_date} />
                <InfoItem label="Sales Rep" value={lead.sales_rep_name} />
                <InfoItem label="Contact Email" value={lead.contact_email || '—'} />
             </div>
             <div className="pt-4 mt-4 border-t border-gray-50">
                <p className="text-[10px] font-black text-gray-300 uppercase mb-2">Notes</p>
                <p className="text-xs text-gray-600 italic">"{lead.notes || 'No notes available.'}"</p>
             </div>
          </div>
        </div>

        {/* Right Column: Meetings */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-black text-gray-900 tracking-tight">Meeting Logs</h2>
            <button onClick={() => setShowMeetModal(true)} className="flex items-center gap-1.5 text-primary text-xs font-black uppercase tracking-widest hover:underline">
              <Plus className="w-4 h-4" /> Log New Meeting
            </button>
          </div>

          {meetings.length === 0 ? (
            <div className="bg-white p-12 text-center rounded-card border border-dashed text-gray-400">
               No meetings recorded for this lead.
            </div>
          ) : (
            <div className="space-y-3">
              {meetings.map((m) => (
                <div key={m.id} className="bg-white p-5 rounded-card border border-gray-100 shadow-soft space-y-3">
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-gray-50 rounded-lg text-gray-400"><Calendar className="w-4 h-4" /></div>
                      <div>
                        <p className="text-sm font-bold text-gray-900">{m.meeting_date} at {m.meeting_time}</p>
                        <p className="text-[10px] text-gray-400 uppercase font-bold tracking-tighter">{m.platform}</p>
                      </div>
                    </div>
                    {m.requires_ceo && <span className="bg-amber-50 text-amber-600 px-2 py-1 rounded-md text-[10px] font-black uppercase">CEO Required</span>}
                  </div>
                  <p className="text-xs text-gray-600 leading-relaxed bg-gray-50/50 p-3 rounded-xl border border-gray-50">"{m.outcome_notes}"</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Meeting Modal */}
      {showMeetModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-card shadow-elevated w-full max-w-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-50 flex items-center justify-between font-bold text-gray-900">
              Log Meeting Details
              <button onClick={() => setShowMeetModal(false)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <form onSubmit={handleLogMeeting} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1.5">Date</label>
                  <input type="date" name="date" required max="2099-12-31" className="w-full p-2.5 border rounded-xl text-sm outline-none" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1.5">Time</label>
                  <input type="time" name="time" required className="w-full p-2.5 border rounded-xl text-sm outline-none" />
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1.5">Platform</label>
                <select name="platform" className="w-full p-2.5 border rounded-xl text-sm outline-none">
                  <option value="Google Meet">Google Meet</option>
                  <option value="Zoom">Zoom</option>
                  <option value="In-person">In-person</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1.5">Attendees (comma separated)</label>
                <input name="attendees" placeholder="Client Name, Sales Rep Name" className="w-full p-2.5 border rounded-xl text-sm outline-none" />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1.5">Outcome Notes</label>
                <textarea name="outcome_notes" required className="w-full p-3 border rounded-xl text-sm outline-none h-24" />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" name="requires_ceo" id="ceo" className="w-4 h-4 accent-primary" />
                <label htmlFor="ceo" className="text-xs font-bold text-gray-600">Requires CEO Participation</label>
              </div>
              <button type="submit" disabled={submitting} className="w-full bg-primary text-white py-3 rounded-xl font-bold flex justify-center items-center gap-2">
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                Save Log
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const InfoItem = ({ label, value }) => (
  <div>
    <p className="text-[10px] uppercase font-bold text-gray-300">{label}</p>
    <p className="text-sm font-bold text-gray-700 mt-0.5">{value}</p>
  </div>
);

export default LeadDetail;